/**
 * FORMAT PRESETS — the renderer's half of `FORM_FORMAT_PRESETS` (packages/workflow-schema,
 * form-grammar.ts). Each preset is a normaliser, a shape test and a message written for the
 * person typing, not the system ("Your UTR should be 10 digits. You entered 8.").
 *
 * The wire value is what the subject typed, trimmed; presets never rewrite the answer. Spaces
 * and dashes are ignored for the CHECK only, so "4471 08236 9" is a valid UTR and is stored
 * as typed.
 *
 * A preset is one of two kinds. A SHAPE preset checks what was typed and can return a message.
 * A DISPLAY preset checks nothing and can never reject an answer — it exists so the renderer
 * can read the value back in a form the subject understands. The split is typed, not a
 * convention: a display preset has no entry in `RULES`, so adding one without deciding which
 * kind it is does not compile.
 */
import { sicCodeLines } from "./sicCodes.js";

export type ShapeFormatPreset =
  | "crn"
  | "utr"
  | "vat_number"
  | "postcode"
  | "paye_ref"
  | "accounts_office_ref"
  | "ni_number"
  | "sort_code"
  | "account_number"
  | "auth_code";

/** Presets that only change how an answer READS BACK; they never gate a submission. */
export type DisplayFormatPreset = "sic_codes";

export type FormatPreset = ShapeFormatPreset | DisplayFormatPreset;

export const DISPLAY_FORMAT_PRESETS: readonly DisplayFormatPreset[] = ["sic_codes"];

export const FORMAT_PRESETS: readonly FormatPreset[] = [
  "crn", "utr", "vat_number", "postcode", "paye_ref", "accounts_office_ref",
  "ni_number", "sort_code", "account_number", "auth_code",
  ...DISPLAY_FORMAT_PRESETS,
];

interface PresetRule {
  /** What the check runs against: the typed value with spaces/dashes removed, upper-cased. */
  test: (compact: string) => boolean;
  /** The message when the test fails; receives the compact value for a "you entered" detail. */
  message: (compact: string, typed: string) => string;
  /** Inputs that are all digits get a numeric keyboard on phones. */
  numeric?: boolean;
}

const RULES: Record<ShapeFormatPreset, PresetRule> = {
  crn: {
    test: (c) => /^(?:[A-Z]{2}[0-9]{6}|[0-9]{8})$/.test(c),
    message: (c) => c.length !== 8
      ? `A company number is 8 characters. You entered ${c.length}.`
      : "A company number is 8 digits, or two letters followed by 6 digits (for example SC123456).",
  },
  utr: {
    test: (c) => /^[0-9]{10}$/.test(c),
    message: (c) => /^[0-9]*$/.test(c)
      ? `Your UTR should be 10 digits. You entered ${c.length}.`
      : "Your UTR is 10 digits, with no letters.",
    numeric: true,
  },
  vat_number: {
    test: (c) => /^(?:GB)?[0-9]{9}$/.test(c),
    message: () => "A VAT number is 9 digits, sometimes written with GB in front (for example GB123456789).",
  },
  postcode: {
    test: (c) => /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/.test(c),
    message: () => "Enter a full UK postcode, for example BS1 4QP.",
  },
  paye_ref: {
    test: (c) => /^[0-9]{3}\/[A-Z0-9]{1,10}$/.test(c),
    message: () => "A PAYE reference is 3 digits, a slash, then letters and numbers (for example 123/AB45678).",
  },
  accounts_office_ref: {
    test: (c) => /^[0-9]{3}P[A-Z][0-9]{7}[0-9X]$/.test(c),
    message: (c) => c.length !== 13
      ? `An accounts office reference is 13 characters. You entered ${c.length}.`
      : "An accounts office reference looks like 123PA00012345.",
  },
  ni_number: {
    test: (c) => /^[A-CEGHJ-PR-TW-Z]{2}[0-9]{6}[A-D]$/.test(c),
    message: () => "A National Insurance number is 2 letters, 6 digits, then a letter (for example QQ123456C).",
  },
  sort_code: {
    test: (c) => /^[0-9]{6}$/.test(c),
    message: () => "A sort code is 6 digits (for example 12-34-56).",
    numeric: true,
  },
  account_number: {
    test: (c) => /^[0-9]{8}$/.test(c),
    message: (c) => `An account number is 8 digits. You entered ${c.length}.`,
    numeric: true,
  },
  auth_code: {
    test: (c) => /^[A-Z0-9]{6}$/.test(c),
    message: (c) => `The authentication code is 6 characters. You entered ${c.length}.`,
  },
};

export function isFormatPreset(value: unknown): value is FormatPreset {
  return typeof value === "string" && (FORMAT_PRESETS as readonly string[]).includes(value);
}

/** True for a preset that only reads a value back; it has no shape rule and never rejects. */
export function isDisplayFormatPreset(value: unknown): value is DisplayFormatPreset {
  return typeof value === "string" && (DISPLAY_FORMAT_PRESETS as readonly string[]).includes(value);
}

/** Space-and-dash-insensitive, upper-cased view of what was typed. */
export function compactForFormat(typed: string): string {
  return typed.replace(/[\s-]/g, "").toUpperCase();
}

/** `undefined` when the value conforms (or is empty — `required` owns emptiness). */
export function formatError(preset: FormatPreset, typed: string): string | undefined {
  // A display preset has nothing to say about what was typed. Returning a message here would
  // turn a reading aid into a submission gate.
  if (isDisplayFormatPreset(preset)) return undefined;
  const compact = compactForFormat(typed);
  if (compact.length === 0) return undefined;
  const rule = RULES[preset];
  return rule.test(compact) ? undefined : rule.message(compact, typed);
}

export function formatIsNumeric(preset: FormatPreset): boolean {
  return !isDisplayFormatPreset(preset) && RULES[preset].numeric === true;
}

/**
 * How each DISPLAY preset reads its answer back: the value as entered in, lines out. The map
 * is keyed by the preset union, so a new display preset that nobody taught to read itself back
 * does not compile — the failure a `switch` with no `default` would have let through silently.
 */
const DISPLAY_READBACK: Record<DisplayFormatPreset, (typed: string) => string[]> = {
  sic_codes: sicCodeLines,
};

/**
 * The read-back lines for a field's current answer, or `undefined` when there are none — the
 * ONE place a declared `format:` turns a value into lines, shared by the field row (under the
 * input, as the subject types) and the review step (the same answer, read back to confirm).
 *
 * This derives a VIEW and nothing else. The answer the caller holds is untouched, so no
 * description can reach the submitted payload.
 */
export function formatHints(format: unknown, value: unknown): string[] | undefined {
  if (!isDisplayFormatPreset(format)) return undefined;
  const lines = DISPLAY_READBACK[format](typeof value === "string" ? value : "");
  return lines.length > 0 ? lines : undefined;
}

/** Anchored custom pattern; a malformed pattern never throws at the subject. */
export function patternError(pattern: string, message: string | undefined, typed: string): string | undefined {
  const value = typed.trim();
  if (value.length === 0) return undefined;
  try {
    const re = new RegExp(`^(?:${pattern})$`);
    return re.test(value) ? undefined : (message?.trim() || "Check this answer and try again.");
  } catch {
    return undefined;
  }
}
